/* URL <-> filter state for the document queues. No API hooks: the console adds the company, the portal does not. */

import { FAMILIES, INBOUND_TYPE_LABEL, TYPE_CODE_LABEL, familyLabel } from "../../domain/families";
import { localizedOptions } from "../../i18n/localized";
import { stripStages, type Direction } from "./StageStrip";

export type QueueFilters = {
  direction: Direction;
  stage: string[];
  state: string;
  from: string;
  to: string;
  q: string;
  /** One family key (HUB-F08 `?family=`); only an outbound family, never "inbound". */
  family: string;
  /** UBL type codes (HUB-F08 `?typeCode=`), sent as a comma list. */
  typeCode: string[];
};

export const QUERY_DEBOUNCE_MS = 250;

/** m4-rulings.md #50: outbound is in_flight|done|error, inbound is in_flight|held|landed. */
export const STATE_OPTIONS: Record<Direction, { value: string; label: string }[]> = {
  out: localizedOptions("domain.state", [
    { value: "in_flight", label: "In flight" },
    { value: "done", label: "Done" },
    { value: "error", label: "Error" },
  ]),
  in: localizedOptions("domain.state", [
    { value: "in_flight", label: "In flight" },
    { value: "held", label: "Held" },
    { value: "landed", label: "Landed" },
  ]),
};

export const DIRECTIONS: { value: Direction; label: string }[] = localizedOptions("domain.direction", [
  { value: "out" as Direction, label: "Outbound" },
  { value: "in" as Direction, label: "Inbound" },
]);

/** Every code the families emit, in family order, each once: 380, 381, 383, 389, 261. */
const OUTBOUND_CODES = FAMILIES.flatMap((spec) => spec.typeCodes).filter(
  (code, index, all) => all.indexOf(code) === index,
);

/** The type codes each direction can carry, named as families.ts names them (R2). */
export const TYPE_OPTIONS: Record<Direction, { value: string; label: string }[]> = {
  out: OUTBOUND_CODES.map((value) => ({ value, get label() { return TYPE_CODE_LABEL[value]; } })),
  in: (["380", "381"] as const).map((value) => ({ value, get label() { return INBOUND_TYPE_LABEL[value]; } })),
};

export const FAMILY_OPTIONS: { value: string; label: string }[] = FAMILIES.map((spec) => ({
  value: spec.key,
  get label() {
    return familyLabel(spec.key);
  },
}));

function validTypeCodes(direction: Direction): Set<string> {
  return new Set(TYPE_OPTIONS[direction].map((option) => option.value));
}

function isFamily(value: string): boolean {
  return FAMILIES.some((spec) => spec.key === value);
}

export function readQueueFilters(params: URLSearchParams): QueueFilters {
  const direction: Direction = params.get("direction") === "in" ? "in" : "out";
  const valid = new Set(stripStages(direction));
  const codes = validTypeCodes(direction);
  const family = params.get("family") ?? "";
  return {
    direction,
    stage: (params.get("stage") ?? "").split(",").filter((stage) => valid.has(stage)),
    state: params.get("state") ?? "",
    from: params.get("from") ?? "",
    to: params.get("to") ?? "",
    q: params.get("q") ?? "",
    family: direction === "out" && isFamily(family) ? family : "",
    typeCode: (params.get("typeCode") ?? "").split(",").filter((code) => codes.has(code)),
  };
}

export function writeQueueFilters(filters: QueueFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.direction !== "out") params.set("direction", filters.direction);
  if (filters.stage.length) params.set("stage", filters.stage.join(","));
  for (const key of ["state", "from", "to", "q", "family"] as const) {
    if (filters[key]) params.set(key, filters[key]);
  }
  if (filters.typeCode.length) params.set("typeCode", filters.typeCode.join(","));
  return params;
}

export function isQueueFiltered(filters: QueueFilters): boolean {
  return (
    filters.direction !== "out" ||
    filters.stage.length > 0 ||
    filters.typeCode.length > 0 ||
    Boolean(filters.state || filters.from || filters.to || filters.q || filters.family)
  );
}

/** Merges a change; a direction change drops stages, states, families and type codes that do not exist on the other side. */
export function mergeQueueFilters<T extends QueueFilters>(filters: T, change: Partial<T>): T {
  const merged = { ...filters, ...change };
  if (change.direction && change.direction !== filters.direction) {
    const valid = new Set(stripStages(change.direction));
    merged.stage = merged.stage.filter((stage) => valid.has(stage));
    // The two directions share only `in_flight`, so any other state is dropped too.
    if (merged.state && merged.state !== "in_flight") merged.state = "";
    // Families are an outbound notion; received documents are only ever 380 or 381.
    if (change.direction === "in") merged.family = "";
    const codes = validTypeCodes(change.direction);
    merged.typeCode = merged.typeCode.filter((code) => codes.has(code));
  }
  return merged;
}

export function toggleStage(filters: QueueFilters, stage: string): string[] {
  return filters.stage.includes(stage)
    ? filters.stage.filter((value) => value !== stage)
    : [...filters.stage, stage];
}
